import Button from "../core/Button";
import ResizableScene from "../core/ResizableScene";
import ScaleManager from "../core/ScaleManager";
import Medal from "../game/Medal";
import GameColors from "../GameColors";
import PlayerData from "../PlayerData";
import CertificatePopup from "../ui/CertificatePopup";
import LevelSelectButton from "../ui/LevelSelectButton";
import StoryMoment, { StoryBeat } from "../ui/StoryMoment";
import Fonts from "../utils/Fonts";
import LayoutUtils from "../utils/LayoutUtils";

export default class LevelSelectScene extends ResizableScene {
	private _onResize: () => void;
	container: Phaser.GameObjects.Container;
	buttons: LevelSelectButton[];
	dayButton: Button;
	nightButton: Button;
	nightIcon: Phaser.GameObjects.Image;
	nightText: Phaser.GameObjects.Text;
	private soundButton: Button;
	private isSoundOn: boolean;
	create(): void {
		super.create();
		const saveData = PlayerData.Instance.saveData;
		this.buttons = [];
		const buttons = this.buttons;
		this.isSoundOn = !this.soundManager.muted;

		const container = (this.container = this.add.container(0, 0));

		const bg = this.add.image(0, 0, "levelSelectBG");
		container.add(bg);

		const buttonHolder = this.add.container();
		container.add(buttonHolder);

		const backBtn = this.add.button(0, 0, "backButton", 0, this.back, this, true);
		container.add(backBtn);

		const header = this.add.image(0, 0, "levelHeader");
		header.setOrigin(0.5, 0);
		container.add(header);

		const headerStyle = {
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratBold,
			fontSize: "70px",
		};
		const headerText = this.add.text(0, 0, "MOUNTAIN MAP", headerStyle);
		container.add(headerText);

		const dayNightContainer = this.add.container();
		container.add(dayNightContainer);
		const dayButton = (this.dayButton = this.add.button(0, 0, "dayNightButtonDown", 0, this.setDay, this, true).setScale(1.1));
		const nightButton = (this.nightButton = this.add.button(0, 0, "dayNightButtonUp", 0, this.setNight, this, true).setScale(1.1));
		dayNightContainer.add([dayButton, nightButton]);

		const dayIcon = this.add.image(0, 0, "day").setScale(1.1);
		const nightIcon = (this.nightIcon = this.add.image(0, 0, "night").setScale(1.1));
		dayNightContainer.add([dayIcon, nightIcon]);

		const bodyStyle = {
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratBold,
			fontSize: "36px",
		};
		const dayText = this.add.text(0, 0, "Day", bodyStyle);
		const nightText = (this.nightText = this.add.text(0, 0, "Night", bodyStyle));
		dayNightContainer.add([dayText, nightText]);

		if (!saveData.dayTrainingComplete) {
			nightButton.disableInteractive();
			nightButton.setTint(808080);
			nightIcon.setAlpha(0.5);
			nightText.setAlpha(0.5);
		}

		const soundButton = (this.soundButton = this.add.button(
			0,
			0,
			this.isSoundOn ? "soundBtnOn" : "soundBtnOff",
			null,
			this.onSoundPressed,
			this,
			true
		));
		soundButton.setScale(0.65);

		const certificateButton = this.add.button(
			0,
			0,
			"certificateButton",
			undefined,
			() => {
				this.showCertification(true);
			},
			this,
			true
		);
		container.add(certificateButton);

		const lastLevelMedal = PlayerData.Instance.saveData.lastLevelMedal;
		const nightUnlocked = saveData.medalsEarned[3] != Medal.None ? true : false;
		if (!nightUnlocked) dayNightContainer.setVisible(false);

		const certificateUnlocked = saveData.medalsEarned[7] != Medal.None ? true : false;
		if (!certificateUnlocked) {
			certificateButton.setVisible(false);
			certificateButton.setInteractive(false);
		}

		for (let i = 0; i < 4; i++) {
			const prevIndex = i - 1;
			const levelLocked = i == 0 || saveData.medalsEarned[prevIndex] != Medal.None ? false : true;
			const nightLevelLocked = saveData.medalsEarned[prevIndex + 4] != Medal.None ? false : true;
			const playMedalEarned = nightLevelLocked ? lastLevelMedal == i : lastLevelMedal == i + 4;
			const playUnlock = nightUnlocked ? lastLevelMedal == prevIndex + 4 : lastLevelMedal == prevIndex;
			// console.log("Level " + i + ": " + playMedalEarned + ", " + playUnlock);

			const button = new LevelSelectButton(this, i + 1, levelLocked, nightLevelLocked, playMedalEarned, playUnlock);
			container.add(button);
			buttons.push(button);
		}

		this._onResize = (): void => {
			ScaleManager.ScaleBackgroundFromZone(bg, this.viewport);
			this.alignIn.Center(bg, this.viewport);
			this.alignIn.TopCenter(header, this.viewport);
			this.alignIn.Center(headerText, header);
			this.alignTo.BottomCenter(dayButton, header, -180, 35);
			this.alignTo.BottomCenter(nightButton, header, 180, 35);
			this.alignTo.LeftCenter(dayIcon, dayButton, -dayIcon.width * 0.75);
			this.alignTo.LeftCenter(nightIcon, nightButton, -nightIcon.width * 0.75);
			this.alignIn.TopLeft(soundButton, this.viewport, -150, -5);
			this.alignIn.Center(dayText, dayButton, 0, -5);
			this.alignIn.Center(nightText, nightButton, 0, -5);

			this.alignIn.TopLeft(backBtn, this.viewport, -40, -40);
			this.alignIn.BottomRight(certificateButton, this.viewport, -40, -40);

			buttonHolder.setSize(bg.width, bg.height);
			this.alignIn.Center(buttonHolder, bg);
			const screenRatio = this.viewport.width / this.viewport.height;
			const goalRatio = 16 / 9;
			const ratioDif = screenRatio - goalRatio;
			if (screenRatio < goalRatio) {
				buttons.forEach((button) => {
					button.scale = 1 + ratioDif / 2;
				});
				buttonHolder.scale = buttons[0].displayWidth * 4;
			}

			const buttonSize = new Phaser.Math.Vector2(buttons[1].displayWidth * 0.85, buttons[1].displayHeight / 2);
			LayoutUtils.alignGrid(this, buttons, buttonHolder, buttonSize, 4, 0, 100);
			for (let i = 0; i < 4; i++) {
				buttons[i].y += ((i % 2 == 0 ? -1 : 1) * buttons[i].displayHeight) / 4;
			}
		};
		this.resize();

		// Setup ARIA Accessibility
		backBtn.setARIA("Back");
		soundButton.setARIA("Toggle Sound");
		headerText.setARIA(undefined, true);
		if (dayNightContainer.visible) {
			dayButton.setARIA("Day");
			nightButton.setARIA("Night");
		}
		buttons.forEach((b) => b.setARIA());

		// Show intro popup
		if (saveData.firstTimeUser) {
			const storyBeats: StoryBeat[] = [
				{ text: "StoryIntro1", image: "storyImg1" },
				{ text: "StoryIntro2", image: "storyImg2" },
				{ text: "StoryIntro3", image: "storyImg3" },
			];

			const storyPopup = new StoryMoment(this, storyBeats, false, () => {
				PlayerData.Instance.saveData.firstTimeUser = false;
				PlayerData.Instance.save();
			});
			container.add(storyPopup);
			soundButton.showARIA();
		}

		// Show night training complete or certification earned popups
		if (
			saveData.dayTrainingComplete &&
			saveData.medalsEarned[7] != Medal.None &&
			(!saveData.nightTrainingComplete || !saveData.certificationEarned)
		) {
			let searchAndRescueAchieved = true;
			for (const medal of saveData.medalsEarned) {
				if (Medal.convertToNumber(medal) < Medal.convertToNumber(Medal.GOLD)) {
					searchAndRescueAchieved = false;
					break;
				}
			}

			if (!searchAndRescueAchieved && !saveData.nightTrainingComplete) {
				const storyBeats: StoryBeat[] = [
					{ text: "NightTrainingCompleteText", header: "NightTrainingCompleteHeader", image: "nightCompleteImg" },
				];

				const storyPopup = new StoryMoment(this, storyBeats, true, () => {
					PlayerData.Instance.saveData.nightTrainingComplete = true;
					PlayerData.Instance.save();

					// after showing the night popup, show the certificate popup
					this.showCertification(false);
				});
				container.add(storyPopup);
			}
			// show certificate each time player gets a new best on any level
			// this variable is reset in RecapPopup.ts
			else if (!saveData.certificationEarned) {
				this.showCertification(true);
			}
		}

		let dayLevelsCompleted = true;
		for (let i = 0; i < 4; i++) {
			if (saveData.medalsEarned[i] == Medal.None) dayLevelsCompleted = false;
		}

		if (dayLevelsCompleted && saveData.lastLevelMedal != 3) {
			this.setNight(); //set to night by default if day levels have been completed
		}
	}

	protected onSoundPressed(): void {
		//TODO: Mute sounds
		this.soundManager.muted = !this.soundManager.muted;
		this.sound.mute = this.soundManager.muted;
		this.music.muted = !this.music.muted;
		this.isSoundOn = !this.soundManager.muted;
		if (!this.isSoundOn) this.soundButton.setTexture("soundBtnOff");
		else this.soundButton.setTexture("soundBtnOn");
	}

	showCertification(skipFirstPage: boolean): void {
		const certificatePopup = new CertificatePopup(this, PlayerData.Instance.saveData, () => {
			PlayerData.Instance.saveData.certificationEarned = true;
			PlayerData.Instance.save();
		});
		if (skipFirstPage) certificatePopup.nextPage(); // skip first page
		this.container.add(certificatePopup);
	}

	resize(): void {
		super.resize();
		if (this._onResize != null) this._onResize();
	}

	setDay(): void {
		this.buttons.forEach((button) => {
			button.setDay();
		});
		this.dayButton.setTexture("dayNightButtonDown");
		this.nightButton.setTexture("dayNightButtonUp");
	}

	setNight(): void {
		this.buttons.forEach((button) => {
			button.setNight();
		});
		this.dayButton.setTexture("dayNightButtonUp");
		this.nightButton.setTexture("dayNightButtonDown");
	}

	back(): void {
		this.scene.start("PreloaderScene", { destinationScene: "SplashScene" });
		this.scene.stop(this.scene.key);
	}

	onReportCardPressed(): void {
		this.scene.start("PreloaderScene", { destinationScene: "ReportCardScene" });
		this.scene.stop(this.scene.key);
	}

	shutdown(): void {
		this._onResize = null;
		this.container = null;
		super.shutdown();
	}

	checkDayComplete(): void {
		// Show day training complete popup
		const saveData = PlayerData.Instance.saveData;
		let dayLevelsCompleted = true;
		for (let i = 0; i < 4; i++) {
			if (saveData.medalsEarned[i] == Medal.None) dayLevelsCompleted = false;
		}
		if (!saveData.dayTrainingComplete && dayLevelsCompleted) {
			const storyBeats: StoryBeat[] = [
				{ text: "DayTrainingCompleteText", header: "DayTrainingCompleteHeader", image: "dayCompleteImg" },
			];

			const storyPopup = new StoryMoment(this, storyBeats, true, () => {
				PlayerData.Instance.saveData.dayTrainingComplete = true;
				PlayerData.Instance.save();
			});
			this.container.add(storyPopup);

			this.nightButton.setInteractive();
			this.nightButton.clearTint();
			this.nightIcon.setAlpha(1);
			this.nightText.setAlpha(1);
		}
	}
}
